import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GitCompareArrows, Search, FileText, Loader2, ArrowUpRight, AlertOctagon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import GuardianPortfolioPanel from "../components/qenva/GuardianPortfolioPanel";
import SeverityHistogram from "../components/qenva/SeverityHistogram";
import CrossCheckScoreGauge from "../components/qenva/CrossCheckScoreGauge";
import { useTour } from "../lib/tours/useTour";
import { useCrossCheckSteps } from "../lib/tours/steps/crosscheck";
import TourReplayButton from "../lib/tours/TourReplayButton";

/**
 * QENVA CrossCheck — workspace hub.
 *
 * Lists every comparison in the workspace, side-by-side with an
 * executive-grade Guardian header (variant="crosscheck"), severity
 * histogram, top-exposed documents and a dense, searchable, filterable
 * comparison table designed to scale to hundreds of items.
 */
const SEVERITY_ORDER = ["Critical", "High", "Medium", "Low", "Informational"];
const SEVERITY_RANK = {
  Critical: 0, High: 1, Medium: 2, Low: 3, Informational: 4,
};
const SEVERITY_PILL_CLASS = {
  Critical:      "bg-rose-50 text-rose-700 border-rose-200",
  High:          "bg-orange-50 text-orange-700 border-orange-200",
  Medium:        "bg-amber-50 text-amber-800 border-amber-200",
  Low:           "bg-emerald-50 text-emerald-700 border-emerald-200",
  Informational: "bg-zinc-50 text-zinc-600 border-zinc-200",
};

const PAGE_SIZE = 50;

function findingMeta(comp) {
  const findings = comp.findings || [];
  let open = 0;
  let worstSeverity = "Informational";
  let worstRank = SEVERITY_RANK.Informational;
  for (const f of findings) {
    const isResolved = f.resolved === true || (typeof f.resolved === "object" && f.resolved?.at);
    if (!isResolved) open += 1;
    const sev = f.severity || "Low";
    const r = SEVERITY_RANK[sev] ?? 9;
    if (r < worstRank) {
      worstRank = r;
      worstSeverity = sev;
    }
  }
  return { total: findings.length, open, worstSeverity };
}

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.round(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.round(diff / 3600)} h`;
    if (diff < 604800) return `il y a ${Math.round(diff / 86400)} j`;
    return d.toLocaleDateString();
  } catch (_) {
    return iso.slice(0, 10);
  }
}

export default function CrossCheck() {
  const { t } = useTranslation("crosscheck");
  const [overview, setOverview] = useState(null);
  const [comparisons, setComparisons] = useState([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingList, setLoadingList] = useState(true);

  // Filters
  const [severity, setSeverity] = useState("All");
  const [status, setStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("All");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    setLoadingOverview(true);
    api.get("/comparisons/overview")
      .then((res) => { if (!cancelled) setOverview(res.data); })
      .catch((e) => {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Overview failed");
      })
      .finally(() => { if (!cancelled) setLoadingOverview(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    api.get("/comparisons", { params: { limit: 500 } })
      .then((res) => { if (!cancelled) setComparisons(res.data || []); })
      .catch((e) => {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "List failed");
      })
      .finally(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
  }, []);

  // Derive available types from the comparisons themselves
  const availableTypes = useMemo(() => {
    const set = new Set();
    for (const c of comparisons) {
      if (c.type) set.add(c.type);
    }
    return Array.from(set).sort();
  }, [comparisons]);

  // Filtering pipeline
  const filtered = useMemo(() => {
    const minRank = severity === "All" ? 99 : SEVERITY_RANK[severity];
    const q = search.trim().toLowerCase();
    return comparisons.filter((c) => {
      if (type !== "All" && c.type !== type) return false;
      const meta = findingMeta(c);
      if (status === "open" && meta.open === 0) return false;
      if (status === "resolved" && meta.open === meta.total) return false;
      if (severity !== "All") {
        const w = SEVERITY_RANK[meta.worstSeverity];
        if (w > minRank) return false;
      }
      if (q) {
        const haystack = [
          c.display_name, c.type, c.id, c.doc_a_id, c.doc_b_id,
          ...(c.findings || []).flatMap((f) => [f.label, f.why, f.suggested_fix]),
        ].filter(Boolean).map((s) => String(s).toLowerCase()).join(" ");
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [comparisons, severity, status, search, type]);

  const visible = filtered.slice(0, visibleCount);

  function clearFilters() {
    setSeverity("All");
    setStatus("All");
    setSearch("");
    setType("All");
  }

  const metrics = useMemo(() => {
    const t0 = overview?.totals || { comparisons: 0, findings: 0, findings_open: 0, findings_resolved: 0 };
    const critOpen = overview?.severity_histogram_open?.Critical || 0;
    const typeCounts = overview?.type_counts || {};
    return [
      { key: "comparisons", value: t0.comparisons, label: t("panel.metrics.comparisons"),
        sub: t("panel.metrics.comparisons_sub", { count: Object.keys(typeCounts).length }), tone: "white" },
      { key: "open_findings", value: t0.findings_open, label: t("panel.metrics.open_findings"),
        sub: t("panel.metrics.open_findings_sub"), tone: "info" },
      { key: "critical", value: critOpen, label: t("panel.metrics.critical"),
        sub: t("panel.metrics.critical_sub"), tone: critOpen > 0 ? "danger" : "neutral" },
      { key: "resolved", value: t0.findings_resolved, label: t("panel.metrics.resolved"),
        sub: t("panel.metrics.resolved_sub"), tone: "success" },
    ];
  }, [overview, t]);

  const score = overview?.crosscheck_score;
  const crosscheckSteps = useCrossCheckSteps();
  const { Tour, replay } = useTour("crosscheck", crosscheckSteps);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="crosscheck-page">
      {Tour}
      <div className="flex justify-end">
        <TourReplayButton onClick={replay} />
      </div>
      {/* QENVA Guardian Portfolio header — variant="crosscheck" */}
      <GuardianPortfolioPanel
        testId="crosscheck-panel"
        variant="crosscheck"
        kicker={t("panel.kicker")}
        title={t("panel.title")}
        subtitle={t("panel.subtitle")}
        status={{
          state: (overview?.totals?.comparisons || 0) === 0 ? "paused" : "live",
          label: (overview?.totals?.comparisons || 0) === 0
            ? t("panel.status_paused_label")
            : t("panel.status_live_label"),
        }}
        metrics={metrics}
        loading={loadingOverview}
        leadingSlot={score ? (
          <CrossCheckScoreGauge
            value={score.value}
            band={score.band}
            bandLabel={t(`panel.score_band.${score.band}`).toUpperCase()}
            label={t("panel.score_label")}
          />
        ) : null}
      />

      {/* Histogram + Top documents (two-column) */}
      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-4">
        <section
          className="rounded-2xl border border-zinc-200 bg-white p-5"
          data-testid="crosscheck-histogram-card"
        >
          <header className="mb-4">
            <p className="qenva-kicker !text-qenva-green">{t("histogram.title")}</p>
            <h3 className="font-display font-bold text-qenva-navy text-[15px] mt-1 tracking-tight">{t("histogram.subtitle")}</h3>
          </header>
          <div className="rounded-xl bg-qenva-navy p-4">
            <SeverityHistogram
              histogram={overview?.severity_histogram_open || {}}
              testId="crosscheck-histogram-open"
            />
          </div>
        </section>

        <section
          className="rounded-2xl border border-zinc-200 bg-white p-5"
          data-testid="crosscheck-top-docs-card"
        >
          <header className="mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="qenva-kicker !text-qenva-green">{t("top_docs.title")}</p>
              <h3 className="font-display font-bold text-qenva-navy text-[15px] mt-1 tracking-tight">{t("top_docs.subtitle")}</h3>
            </div>
          </header>
          {(overview?.top_docs_with_findings || []).length === 0 ? (
            <p className="text-zinc-400 text-[13px] py-6 text-center" data-testid="crosscheck-top-docs-empty">{t("top_docs.empty")}</p>
          ) : (
            <table className="w-full text-[13px]" data-testid="crosscheck-top-docs-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 font-bold">
                  <th className="pb-2 pr-2">{t("top_docs.filename_header")}</th>
                  <th className="pb-2 pr-2">{t("top_docs.type_header")}</th>
                  <th className="pb-2 pr-2 text-right">{t("top_docs.open_header")}</th>
                  <th className="pb-2 pr-2 text-right">{t("top_docs.total_header")}</th>
                  <th className="pb-2 text-right">{t("top_docs.worst_header")}</th>
                </tr>
              </thead>
              <tbody>
                {(overview.top_docs_with_findings || []).map((d) => (
                  <tr key={d.doc_id} className="border-t border-zinc-100" data-testid={`crosscheck-top-doc-${d.doc_id}`}>
                    <td className="py-2 pr-2 min-w-0">
                      <Link to={`/analysis/${d.doc_id}`} className="text-zinc-900 hover:text-qenva-blue font-medium truncate inline-flex items-center gap-1 max-w-[280px]" title={d.filename}>
                        <FileText size={12} className="text-zinc-400 shrink-0" />
                        <span className="truncate">{d.filename}</span>
                      </Link>
                    </td>
                    <td className="py-2 pr-2 text-[11.5px] text-zinc-500 uppercase tracking-wide">{d.document_type}</td>
                    <td className="py-2 pr-2 text-right font-bold text-qenva-navy tabular-nums">{d.open}</td>
                    <td className="py-2 pr-2 text-right text-zinc-500 tabular-nums">{d.total}</td>
                    <td className="py-2 text-right">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-bold uppercase border ${SEVERITY_PILL_CLASS[d.worst_severity] || SEVERITY_PILL_CLASS.Low}`}>
                        {d.worst_severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      {/* Comparison list — sticky filter row + dense table */}
      <section className="rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="crosscheck-list-card">
        <header className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="qenva-kicker !text-qenva-green">{t("list.title")}</p>
            <h3 className="font-display font-bold text-qenva-navy text-[15px] mt-1 tracking-tight">{t("list.subtitle")}</h3>
          </div>
          <p className="text-[11.5px] text-zinc-500 self-end" data-testid="crosscheck-list-count">
            {t("filters.results_count", { count: filtered.length, total: comparisons.length })}
          </p>
        </header>

        {/* Filter row */}
        <div className="px-5 py-3 bg-zinc-50/60 border-b border-zinc-200 flex items-center gap-2 flex-wrap" data-testid="crosscheck-filters">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("filters.search_placeholder")}
              data-testid="crosscheck-filter-search"
              className="w-full pl-7.5 pl-8 pr-3 py-1.5 rounded-lg border border-zinc-200 text-[12.5px] focus:outline-none focus:border-qenva-blue/60 focus:ring-2 focus:ring-qenva-blue/15"
            />
          </div>
          <FilterSelect
            label={t("filters.severity")}
            value={severity}
            onChange={setSeverity}
            options={[{ value: "All", label: t("filters.severity_all") }, ...SEVERITY_ORDER.map((s) => ({ value: s, label: s }))]}
            testId="crosscheck-filter-severity"
          />
          <FilterSelect
            label={t("filters.status")}
            value={status}
            onChange={setStatus}
            options={[
              { value: "All", label: t("filters.status_all") },
              { value: "open", label: t("filters.status_open") },
              { value: "resolved", label: t("filters.status_resolved") },
            ]}
            testId="crosscheck-filter-status"
          />
          {availableTypes.length > 0 && (
            <FilterSelect
              label={t("filters.type")}
              value={type}
              onChange={setType}
              options={[{ value: "All", label: t("filters.type_all") }, ...availableTypes.map((tt) => ({ value: tt, label: tt }))]}
              testId="crosscheck-filter-type"
            />
          )}
          {(severity !== "All" || status !== "All" || search || type !== "All") && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="crosscheck-clear-filters"
              className="text-[11.5px] font-semibold text-qenva-blue hover:underline"
            >
              {t("filters.clear")}
            </button>
          )}
        </div>

        {/* Table */}
        {loadingList ? (
          <div className="grid place-items-center py-16" data-testid="crosscheck-list-loading">
            <Loader2 size={18} className="animate-spin text-zinc-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-16 text-center" data-testid="crosscheck-list-empty">
            <GitCompareArrows size={32} className="text-zinc-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="font-display font-bold text-qenva-navy text-[15px]">{t("list.empty_title")}</p>
            <p className="text-zinc-500 text-[12.5px] mt-1 max-w-md mx-auto">{t("list.empty_subtitle")}</p>
          </div>
        ) : (
          <>
            <table className="w-full text-[13px]" data-testid="crosscheck-list-table">
              <thead className="bg-zinc-50/60 sticky top-0 z-10">
                <tr className="text-left text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 font-bold">
                  <th className="px-5 py-2">{t("list.comparison_header")}</th>
                  <th className="px-3 py-2">{t("list.type_header")}</th>
                  <th className="px-3 py-2 text-right">{t("list.findings_header")}</th>
                  <th className="px-3 py-2">{t("list.severity_header")}</th>
                  <th className="px-3 py-2">{t("list.date_header")}</th>
                  <th className="px-5 py-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const meta = findingMeta(c);
                  return (
                    <tr
                      key={c.id}
                      className="border-t border-zinc-100 hover:bg-zinc-50/60 transition-colors"
                      data-testid={`crosscheck-row-${c.id}`}
                    >
                      <td className="px-5 py-3 min-w-0 max-w-[420px]">
                        <p className="font-semibold text-qenva-navy text-[13px] truncate" title={c.display_name || c.type}>
                          {c.display_name || c.type}
                        </p>
                        <p className="text-[11px] text-zinc-500 mt-0.5 truncate font-mono">
                          {(c.doc_a_id || "").slice(0, 8)} ⇄ {(c.doc_b_id || "").slice(0, 8)}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-[11.5px] text-zinc-500 uppercase tracking-wide">{c.type}</td>
                      <td className="px-3 py-3 text-right">
                        <span className="font-bold text-qenva-navy text-[14px] tabular-nums">{meta.open}</span>
                        <span className="text-zinc-400 text-[11.5px] tabular-nums">&nbsp;/ {meta.total}</span>
                      </td>
                      <td className="px-3 py-3">
                        {meta.total > 0 ? (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-bold uppercase border ${SEVERITY_PILL_CLASS[meta.worstSeverity] || SEVERITY_PILL_CLASS.Low}`}>
                            {meta.worstSeverity}
                          </span>
                        ) : (
                          <span className="text-zinc-400 text-[11px]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-zinc-500 text-[11.5px]">{formatRelative(c.created_at)}</td>
                      <td className="px-5 py-3 text-right">
                        <Link
                          to={`/comparisons/${c.id}`}
                          data-testid={`crosscheck-open-${c.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11.5px] font-semibold text-qenva-navy hover:bg-qenva-blue/10 hover:text-qenva-blue transition-colors"
                        >
                          {t("list.open_cta")}
                          <ArrowUpRight size={12} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length > visibleCount && (
              <div className="px-5 py-4 border-t border-zinc-100 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  data-testid="crosscheck-show-more"
                  className="text-[12.5px] font-semibold text-qenva-blue hover:underline"
                >
                  {t("list.show_more")} ({filtered.length - visibleCount})
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-500 font-semibold">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="px-2.5 py-1 rounded-lg border border-zinc-200 bg-white text-[12px] font-medium text-zinc-900 focus:outline-none focus:border-qenva-blue/60 focus:ring-2 focus:ring-qenva-blue/15"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
